import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";
import { STAGE_LABEL, fmtDateTime } from "./constants";
import { ModalShell, Field } from "../_dialog";

export default function CalendarView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/founder/meetings");
      setItems(r.data?.meetings || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const remove = async (m) => {
    if (!window.confirm("Delete this meeting?")) return;
    try {
      await api.delete(`/founder/meetings/${m.id}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const grouped = useMemo(() => {
    const upcoming = [];
    const past = [];
    const now = new Date();
    for (const m of items) {
      try {
        const d = new Date(m.starts_at);
        if (d >= now) upcoming.push(m);
        else past.push(m);
      } catch {
        upcoming.push(m);
      }
    }
    return { upcoming, past };
  }, [items]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="founder-os-calendar">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {grouped.upcoming.length} upcoming · {grouped.past.length} past
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="meeting-create-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> Schedule
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {grouped.upcoming.length === 0 && (
          <div className="text-xs text-slate-500 p-6 text-center">Nothing upcoming.</div>
        )}
        {grouped.upcoming.map((m) => (
          <MeetingRow key={m.id} m={m} onEdit={() => setEditing(m)} onDelete={() => remove(m)} />
        ))}
      </div>

      {grouped.past.length > 0 && (
        <details className="rounded-xl border border-slate-200 bg-white">
          <summary className="cursor-pointer p-3 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            Past · {grouped.past.length}
          </summary>
          <div className="divide-y divide-slate-100">
            {grouped.past.map((m) => (
              <MeetingRow key={m.id} m={m} onEdit={() => setEditing(m)} onDelete={() => remove(m)} muted />
            ))}
          </div>
        </details>
      )}

      {creating && (
        <MeetingDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <MeetingDialog
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function MeetingRow({ m, onEdit, onDelete, muted }) {
  return (
    <div className="flex items-start gap-3 p-3" data-testid={`meeting-row-${m.id}`}>
      <div className="text-[10px] uppercase tracking-wider rounded-full bg-indigo-50 text-indigo-700 px-2 py-1 font-semibold whitespace-nowrap">
        {STAGE_LABEL(m.kind)}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold truncate ${muted ? "text-slate-500" : "text-slate-900"}`}>
          {m.title}
        </div>
        <div className="text-xs text-slate-500">
          {fmtDateTime(m.starts_at)} · {m.duration_minutes || 30} min
        </div>
        {m.notes && (
          <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{m.notes}</div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          data-testid={`meeting-edit-${m.id}`}
          className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid={`meeting-delete-${m.id}`}
          className="text-rose-500 hover:bg-rose-50 rounded p-1"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function MeetingDialog({ record, onClose, onSaved }) {
  const initialDate = record?.starts_at
    ? new Date(record.starts_at).toISOString().slice(0, 16)
    : "";
  const [form, setForm] = useState({
    title: record?.title || "",
    kind: record?.kind || "follow_up",
    starts_at: initialDate,
    duration_minutes: record?.duration_minutes || 30,
    notes: record?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim() || !form.starts_at) {
      toast.error("Title and start time are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 30,
      };
      if (record) {
        await api.patch(`/founder/meetings/${record.id}`, payload);
        toast.success("Meeting updated.");
      } else {
        await api.post("/founder/meetings", payload);
        toast.success("Meeting scheduled.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={record ? "Edit meeting" : "Schedule meeting"}
      eyebrow="Calendar"
      onClose={onClose}
      testId="meeting-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="meeting-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="meeting-dialog-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <Field label="Title">
        <input
          type="text"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          autoFocus
          data-testid="meeting-dialog-title"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Kind">
          <select
            value={form.kind}
            onChange={(e) => set("kind", e.target.value)}
            data-testid="meeting-dialog-kind"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="demo">Demo</option>
            <option value="follow_up">Follow-up</option>
            <option value="vendor_call">Vendor call</option>
            <option value="milestone">Milestone</option>
          </select>
        </Field>
        <Field label="Duration (min)">
          <input
            type="number"
            min="5"
            value={form.duration_minutes}
            onChange={(e) => set("duration_minutes", e.target.value)}
            data-testid="meeting-dialog-duration"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
        </Field>
      </div>
      <Field label="Starts at">
        <input
          type="datetime-local"
          value={form.starts_at}
          onChange={(e) => set("starts_at", e.target.value)}
          data-testid="meeting-dialog-starts"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <Field label="Notes">
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="meeting-dialog-notes"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
    </ModalShell>
  );
}

// ===========================================================================
// Shared modal shell + Field
